'use client';

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { cn } from '@/src/shared/lib/utils';

export interface FilterState {
  location?: string;
  useMyLocation?: boolean;
  radius?: '5' | '10' | '20';
  priceType?: 'per-service' | 'per-hour';
  costMin?: string;
  costMax?: string;
  rating?: number;
  minReviews?: number;
  hasPortfolio?: boolean;
  hasCertificates?: boolean;
}

interface MapFilterPanelProps {
  onFilterChange?: (filters: FilterState) => void;
  className?: string;
  initialFilters?: Partial<FilterState>;
}

export const MapFilterPanel = ({
  onFilterChange,
  className,
  initialFilters,
}: MapFilterPanelProps) => {
  const [filters, setFilters] = useState<FilterState>({
    // Don't set defaults - only apply filters that user explicitly selects
    // useMyLocation is false by default - user must explicitly enable it
    // Radius is 5km by default but only applied when useMyLocation is enabled
    radius: '5',
    ...initialFilters,
  });

  // Update location when initialFilters change (e.g., when profile loads)
  useEffect(() => {
    if (initialFilters?.location && !filters.location) {
      setFilters((prev) => ({
        ...prev,
        location: initialFilters.location,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters?.location]);

  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K],
    event?: React.MouseEvent | React.ChangeEvent
  ) => {
    console.log('[MapFilterPanel] updateFilter called:', { key, value, filters });

    // Prevent event bubbling to avoid unexpected side effects
    if (event) {
      event.stopPropagation();
    }
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    console.log('[MapFilterPanel] Calling onFilterChange with:', newFilters);
    onFilterChange?.(newFilters);
  };

  const resetFilters = () => {
    const resetState: FilterState = {
      // Keep location from initial filters if available
      ...(initialFilters?.location && { location: initialFilters.location }),
      // Keep default radius
      radius: '5',
    };
    setFilters(resetState);
    onFilterChange?.(resetState);
  };

  return (
    <div className={cn('px-2.5 pt-5 bg-background-primary flex', className)} style={{ overscrollBehavior: 'contain' }}>
      <div className="flex-1 flex flex-col gap-7 overflow-y-auto pr-2" style={{ overscrollBehavior: 'contain' }}>
        {/* Location Section */}
        <div className="flex flex-col gap-2.5">
          <div className="text-text-primary text-xl font-normal leading-8">
            Location
          </div>
          <div className="flex flex-col gap-3.5">
            {/* Location Input */}
            <div className="h-12 pl-5 pr-3 py-3 bg-background-primary rounded-full shadow-[inset_0px_-1px_10px_0px_rgba(42,42,15,0.10)] border border-border-primary focus-within:border-accent-primary transition-colors flex items-center">
              <input
                type="text"
                placeholder="Enter city or postcode"
                value={filters.location || ''}
                onChange={(e) => updateFilter('location', e.target.value)}
                disabled={filters.useMyLocation}
                className="flex-1 bg-transparent text-text-primary text-base font-normal leading-5 outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 placeholder:text-text-secondary disabled:opacity-50"
              />
            </div>
            {/* Use my location checkbox */}
            <label className="inline-flex items-center gap-2.5 cursor-pointer">
              <div className="w-5 h-5 relative">
                <div
                  className={cn(
                    'w-5 h-5 rounded-[5px] transition-colors',
                    filters.useMyLocation
                      ? 'bg-accent-primary'
                      : 'bg-background-primary border border-accent-primary'
                  )}
                />
                {filters.useMyLocation && (
                  <svg
                    className="w-3 h-3 absolute left-1 top-1.5 text-white"
                    viewBox="0 0 12 10"
                    fill="currentColor"
                  >
                    <path d="M1 5L4.5 8.5L11 1.5" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                )}
              </div>
              <input
                type="checkbox"
                checked={filters.useMyLocation}
                onChange={(e) => updateFilter('useMyLocation', e.target.checked, e)}
                className="sr-only"
              />
              <span className="text-text-primary text-base font-normal leading-5">
                Use my location
              </span>
            </label>
          </div>
        </div>

        {/* Radius Section */}
        <div className="flex flex-col gap-5">
          <div className={cn(
            "text-text-primary text-xl font-normal leading-8",
            !filters.useMyLocation && "opacity-50"
          )}>
            Radius
          </div>
          <div className="flex flex-col gap-5">
            {(['5', '10', '20'] as const).map((radius) => (
              <label
                key={radius}
                className={cn(
                  "inline-flex items-center gap-2.5",
                  filters.useMyLocation ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                )}
                onClick={(e) => {
                  if (!filters.useMyLocation) {
                    e.preventDefault();
                    e.stopPropagation();
                    toast.error('Please enable "Use my location" to set radius');
                  }
                }}
              >
                <div className="w-4 h-4 relative">
                  <div className={cn(
                    "w-4 h-4 bg-background-primary rounded-full border",
                    filters.useMyLocation ? "border-accent-primary" : "border-text-secondary"
                  )} />
                  {filters.radius === radius && filters.useMyLocation && (
                    <div className="w-3 h-3 absolute left-[2px] top-[2px] bg-accent-primary rounded-full" />
                  )}
                </div>
                <input
                  type="radio"
                  name="radius"
                  value={radius}
                  checked={filters.radius === radius}
                  onChange={(e) => updateFilter('radius', radius, e)}
                  disabled={!filters.useMyLocation}
                  className="sr-only"
                />
                <span className="text-text-primary text-base font-normal leading-5">
                  {radius} km of my location
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Price Section */}
        <div className="flex flex-col gap-2.5">
          <div className="text-text-primary text-xl font-normal leading-8">
            Price
          </div>
          <div className="flex flex-col gap-[5px]">
            {/* Min Cost */}
            <div className="h-12 px-5 py-3 bg-background-primary rounded-full shadow-[inset_0px_-1px_10px_0px_rgba(42,42,15,0.10)] border border-border-primary focus-within:border-accent-primary transition-colors flex items-center">
              <input
                type="number"
                placeholder="From $"
                value={filters.costMin || ''}
                onChange={(e) => updateFilter('costMin', e.target.value)}
                className="flex-1 bg-transparent text-text-primary text-base font-normal leading-5 outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 placeholder:text-text-secondary"
              />
            </div>
            {/* Max Cost */}
            <div className="h-12 px-5 py-3 bg-background-primary rounded-full shadow-[inset_0px_-1px_10px_0px_rgba(42,42,15,0.10)] border border-border-primary focus-within:border-accent-primary transition-colors flex items-center">
              <input
                type="number"
                placeholder="To $"
                value={filters.costMax || ''}
                onChange={(e) => updateFilter('costMax', e.target.value)}
                className="flex-1 bg-transparent text-text-primary text-base font-normal leading-5 outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 placeholder:text-text-secondary"
              />
            </div>
            {/* Price Type Dropdown */}
            <div className="h-12 px-5 py-3 bg-background-primary rounded-full shadow-[inset_0px_-1px_10px_0px_rgba(42,42,15,0.10)] border border-border-primary focus-within:border-accent-primary transition-colors flex items-center justify-between">
              <select
                value={filters.priceType || 'per-service'}
                onChange={(e) => updateFilter('priceType', e.target.value as 'per-service' | 'per-hour')}
                className="flex-1 bg-transparent text-text-primary text-base font-normal leading-5 outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 appearance-none cursor-pointer"
              >
                <option value="per-service">per Service</option>
                <option value="per-hour">per Hour</option>
              </select>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path
                  d="M10 12L16 18L22 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-text-primary"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Rating Section */}
        <div className="flex flex-col gap-5">
          <div className="text-text-primary text-xl font-normal leading-8">
            Rating
          </div>
          <div className="flex flex-col gap-3.5">
            {[3, 4, 5].map((rating) => (
              <label key={rating} className="inline-flex items-center gap-5 cursor-pointer">
                <div className="flex items-center gap-2.5">
                  <div className="w-4 h-4 relative">
                    <div className="w-4 h-4 bg-background-primary rounded-full border border-accent-primary" />
                    {filters.rating === rating && (
                      <div className="w-3 h-3 absolute left-[2px] top-[2px] bg-accent-primary rounded-full" />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="rating"
                    value={rating}
                    checked={filters.rating === rating}
                    onChange={(e) => updateFilter('rating', rating, e)}
                    className="sr-only"
                  />
                  <span className="text-text-primary text-base font-normal leading-5">
                    {rating}+
                  </span>
                </div>
                <div className="flex">
                  {Array.from({ length: rating }).map((_, i) => (
                    <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                        fill="#6AB344"
                        stroke="#6AB344"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ))}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Reviews Section */}
        <div className="flex flex-col gap-5">
          <div className="text-text-primary text-xl font-normal leading-8">
            Reviews
          </div>
          <div className="flex flex-col gap-5">
            {[5, 10, 20].map((reviews) => (
              <label key={reviews} className="inline-flex items-center gap-2.5 cursor-pointer">
                <div className="w-4 h-4 relative">
                  <div className="w-4 h-4 bg-background-primary rounded-full border border-accent-primary" />
                  {filters.minReviews === reviews && (
                    <div className="w-3 h-3 absolute left-[2px] top-[2px] bg-accent-primary rounded-full" />
                  )}
                </div>
                <input
                  type="radio"
                  name="reviews"
                  value={reviews}
                  checked={filters.minReviews === reviews}
                  onChange={(e) => updateFilter('minReviews', reviews, e)}
                  className="sr-only"
                />
                <span className="text-text-primary text-base font-normal leading-5">
                  {reviews}+ reviews
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Certification Section */}
        <div className="flex flex-col gap-5 pb-5">
          <div className="text-text-primary text-xl font-normal leading-8">
            Certification
          </div>
          <div className="flex flex-col gap-5">
            {/* Filled Portfolio */}
            <label className="inline-flex items-center gap-2.5 cursor-pointer">
              <div className="w-5 h-5 relative">
                <div
                  className={cn(
                    'w-5 h-5 rounded-[5px] transition-colors',
                    filters.hasPortfolio
                      ? 'bg-accent-primary'
                      : 'bg-background-primary border border-accent-primary'
                  )}
                />
                {filters.hasPortfolio && (
                  <svg
                    className="w-3 h-3 absolute left-1 top-1.5 text-white"
                    viewBox="0 0 12 10"
                    fill="currentColor"
                  >
                    <path d="M1 5L4.5 8.5L11 1.5" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                )}
              </div>
              <input
                type="checkbox"
                checked={filters.hasPortfolio}
                onChange={(e) => updateFilter('hasPortfolio', e.target.checked, e)}
                className="sr-only"
              />
              <span className="text-text-primary text-base font-normal leading-5">
                Filled Portfolio
              </span>
            </label>
            {/* Certificates/Diplomas */}
            <label className="inline-flex items-center gap-2.5 cursor-pointer">
              <div className="w-5 h-5 relative">
                <div
                  className={cn(
                    'w-5 h-5 rounded-[5px] transition-colors',
                    filters.hasCertificates
                      ? 'bg-accent-primary'
                      : 'bg-background-primary border border-accent-primary'
                  )}
                />
                {filters.hasCertificates && (
                  <svg
                    className="w-3 h-3 absolute left-1 top-1.5 text-white"
                    viewBox="0 0 12 10"
                    fill="currentColor"
                  >
                    <path d="M1 5L4.5 8.5L11 1.5" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                )}
              </div>
              <input
                type="checkbox"
                checked={filters.hasCertificates}
                onChange={(e) => updateFilter('hasCertificates', e.target.checked, e)}
                className="sr-only"
              />
              <span className="text-text-primary text-base font-normal leading-5">
                Certificates/Diplomas
              </span>
            </label>
          </div>
        </div>

        {/* Reset Filters Button */}
        <div className="pt-2 pb-5">
          <button
            onClick={resetFilters}
            className="w-full h-12 px-5 py-3 bg-background-secondary hover:bg-background-secondary/80 rounded-full transition-colors flex items-center justify-center"
          >
            <span className="text-text-primary text-base font-medium leading-5">
              Reset Filters
            </span>
          </button>
        </div>
      </div>

      {/* Scrollbar indicator */}
      {/* <div className="w-2 flex-shrink-0">
        <div className="w-2 h-24 bg-accent-primary rounded-full" />
      </div> */}
    </div>
  );
};

MapFilterPanel.displayName = 'MapFilterPanel';
